// Helpers for tracking sales pipeline state on the `estimates` collection.
// In this codebase a "lead" is an estimate document with a `leadStatus` field
// (see leadStatuses in estimateData.js). These helpers maintain a `stageHistory`
// array (`{stage, enteredAt}`) that records every change to `leadStatus` so the
// analytics dashboard can compute time-in-stage and stalled-lead metrics.

// Infer the effective leadStatus for an estimate that may predate explicit tracking.
// Mirrors the logic in pages/Leads.js:getLeadStatus so backfill matches the UI view.
export function inferLeadStatus(estimate) {
  if (!estimate) return 'new';
  if (estimate.leadStatus) return estimate.leadStatus;
  if (estimate.status === 'pending_review') return 'new';
  if (estimate.status === 'sent') return 'estimate_sent';
  if (estimate.status === 'approved' || estimate.status === 'converted') return 'won';
  if (estimate.status === 'declined') return 'lost';
  return 'new';
}

// Default analytics field values for a brand-new estimate. Callers may override.
export function defaultSalesAnalyticsFields(now = new Date().toISOString()) {
  return {
    source: 'unknown',
    serviceType: 'unknown',
    assignedTo: null,
    serviceArea: 'unknown',
    quoteAmount: null,
    finalJobValue: null,
    lostReason: null,
    leadStatus: 'new',
    stageHistory: [{ stage: 'new', enteredAt: now }],
  };
}

// Best-effort numeric quote amount derived from existing estimate fields.
// Used for both new-record defaults and the backfill script.
export function deriveQuoteAmount(estimate) {
  if (!estimate) return null;
  const candidates = [
    estimate.quoteAmount,
    estimate.total,
    estimate.totalPrice,
    estimate.approvedTotal,
    estimate.approvedRecurringTotal,
    estimate.approvedDeepCleanTotal,
    estimate.recurringTotal,
    estimate.deepCleanTotal,
    estimate.basePrice,
    estimate.price,
  ];
  for (const c of candidates) {
    const n = Number(c);
    if (Number.isFinite(n) && n > 0) return n;
  }
  if (Array.isArray(estimate.lineItems) && estimate.lineItems.length) {
    const sum = estimate.lineItems.reduce((s, li) => s + (Number(li.amount) || 0), 0);
    return sum > 0 ? sum : null;
  }
  return null;
}

// If `newLeadStatus` differs from the current leadStatus, return a new
// stageHistory array with an appended entry. Otherwise return null (caller
// should leave stageHistory untouched).
export function appendStageEntry(currentEstimate, newLeadStatus, now = new Date().toISOString()) {
  if (!newLeadStatus) return null;
  const current = currentEstimate?.leadStatus;
  if (current === newLeadStatus) return null;
  const existing = Array.isArray(currentEstimate?.stageHistory) ? currentEstimate.stageHistory : [];
  return [...existing, { stage: newLeadStatus, enteredAt: now }];
}

// Build the seed stageHistory for a record that has none, using createdAt as the
// timestamp for the inferred current stage.
export function seedStageHistory(estimate) {
  const stage = inferLeadStatus(estimate);
  const enteredAt = estimate?.createdAt
    || estimate?.submittedAt
    || estimate?.created
    || new Date().toISOString();
  return [{ stage, enteredAt }];
}
